import React, { useEffect, useMemo, useState } from "react";
import "../inward-styles.css";
import Icon from "@mdi/react";
import { mdiCloseCircleOutline } from "@mdi/js";
import {
  SpocModel,
  WarehouseModel,
} from "../../../../erp/inventory/models/warehouse_model";
import { AddWarehouseSpocModal } from "./components/warehouse_muti_spoc_assignment_modal";
import { GetMultiWarehouseSPOCAssigneeAPI } from "./components/warehouse_spoc_mangement_apis";
import { WarehouseInventorySpocModel } from "../../../../erp/inventory/models/inventory_warehouse_spoc_model";
import { useAuth } from "../../../../context_providers/auth/authProvider";
import { ACTION, MODULE } from "../../../../utils/enums";

interface Props {
  warehouse?: WarehouseModel;
  warehouse_spocs?: SpocModel[];
}

interface NormalizedWarehouse {
  warehouse_id: string;
  warehouse_name: string;
  warehouse?: WarehouseModel;
  roles: Record<string, SpocModel[]>;
}

export const WarehouseCard: React.FC<Props> = ({
  warehouse,
  warehouse_spocs = [],
}) => {
  const { isAuthorised } = useAuth();
  const [search, setSearch] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editAssignment, setEditAssignment] = useState<
    WarehouseInventorySpocModel | undefined
  >(undefined);

  const [spocAssignments, setSpocAssignments] = useState<
    WarehouseInventorySpocModel[]
  >([]);
  const [editWarehouse, setEditWarehouse] = useState<WarehouseModel>();
  const [warehouseManager, setWarehouseManager] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await GetMultiWarehouseSPOCAssigneeAPI();
      setSpocAssignments(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const warehosue_manager = isAuthorised({
      module: MODULE.warehouse_manager,
      action: ACTION.CREATE,
    });

    setWarehouseManager(warehosue_manager);
    fetchData();
  }, [isAuthorised]);

  const normalizedWarehouses: NormalizedWarehouse[] = useMemo(() => {
    const map = new Map<string, NormalizedWarehouse>();

    const rows = Array.isArray(spocAssignments) ? spocAssignments : [];

    for (const row of rows) {
      if (!row?.warehouse_id) continue;

      if (!map.has(row.warehouse_id)) {
        map.set(row.warehouse_id, {
          warehouse_id: row.warehouse_id,
          warehouse_name: row.warehouse_name ?? "",
          warehouse: row.warehouse,
          roles: {},
        });
      }

      const wh = map.get(row.warehouse_id)!;

      /* ---------- ROLE HANDLING ---------- */
      if (typeof row.role === "string") {
        if (!wh.roles[row.role]) wh.roles[row.role] = [];
        if (Array.isArray(row.inventory_spocs)) {
          wh.roles[row.role].push(...row.inventory_spocs);
        }
      } else if (typeof row.role === "object" && row.role !== null) {
        for (const [roleName, spocs] of Object.entries(row.role)) {
          if (!Array.isArray(spocs)) continue;

          if (!wh.roles[roleName]) wh.roles[roleName] = [];
          wh.roles[roleName].push(
            ...spocs.map((s: any) => s.inventory_spoc).filter(Boolean),
          );
        }
      }
    }

    return Array.from(map.values());
  }, [spocAssignments]);

  // Filter warehouses by search
  const filteredWarehouses = useMemo(() => {
    const query = search?.trim()?.toLowerCase();
    if (!query) return normalizedWarehouses;

    return normalizedWarehouses
      .map((wh) => ({
        ...wh,
        roles: Object.fromEntries(
          Object.entries(wh.roles).map(([role, spocs]) => [
            role,
            spocs.filter((s: SpocModel) =>
              s.contact_name.toLowerCase().includes(query),
            ),
          ]),
        ),
      }))
      .filter((wh) =>
        Object.values(wh.roles).some((spocs) => spocs.length > 0),
      );
  }, [search, normalizedWarehouses]);

  return (
    <div
      id="warehouse-management-screen"
      className="wh-mobile-shrink space-y-4 p-5"
    >
      <div className="flex font-bold text-3xl">
        <h2>Warehouse Management</h2>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex-1 flex items-center gap-2">
          <input
            id="warehouse-management-search-input"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search SPOCs..."
            className="w-full border rounded-md px-3 py-1.5 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {search && (
            <button
              id="warehouse-management-search-clear-btn"
              onClick={() => setSearch("")}
              className="text-gray-400 hover:text-red-500 transition-colors"
              title="Clear search"
            >
              <Icon path={mdiCloseCircleOutline} size={0.7} />
            </button>
          )}
        </div>
        {warehouseManager && (
          <button
            id="warehouse-management-add-warehouse-manager-btn"
            onClick={() => setOpenModal(true)}
            className="bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-blue-700"
          >
            + Map Warehouse Manager
          </button>
        )}
      </div>

      {/* WAREHOUSE CARDS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredWarehouses.map((wh) => {
          const managerSpoc = wh.roles["manager"];

          return (
            <div
              key={wh.warehouse_id}
              className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition overflow-hidden"
            >
              <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3
                      className="text-lg font-semibold text-gray-900 break-words max-w-full"
                      id={`warehouse-management-card-title-${wh.warehouse_id}`}
                    >
                      {wh.warehouse_name}
                    </h3>

                    {wh.warehouse_id && (
                      <span
                        id={`warehouse-management-card-id-${wh.warehouse_id}`}
                        className="shrink-0 whitespace-nowrap text-xs font-semibold px-3 py-1 rounded-full border border-gray-300 text-gray-700"
                      >
                        {wh.warehouse_id}
                      </span>
                    )}
                  </div>

                  {wh.warehouse?.address && (
                    <div className="flex items-start gap-2 text-sm text-gray-500 mt-2 break-words">
                      <span className="shrink-0">📍</span>
                      <span>
                        {wh.warehouse.address.city},{" "}
                        {wh.warehouse.address.state}
                      </span>
                    </div>
                  )}

                  {wh.warehouse?.address?.line1 && (
                    <p className="text-sm text-gray-600 mt-1 break-words">
                      {wh.warehouse.address.line1}
                    </p>
                  )}
                </div>

                <button
                  id={`warehouse-management-edit-warehouse-btn-${wh.warehouse_id}`}
                  onClick={() => {
                    if (!warehouseManager) return;
                    const assignment = spocAssignments.find(
                      (a) => a.warehouse_id === wh.warehouse_id,
                    );
                    setEditWarehouse(wh.warehouse);
                    setEditAssignment(assignment);
                    setOpenModal(true);
                  }}
                  className="text-gray-500 hover:text-blue-600 shrink-0 self-start sm:self-auto"
                  title="Edit Warehouse"
                  disabled={!warehouseManager}
                >
                  ✏️
                </button>
              </div>

              <hr className="my-4" />

              {managerSpoc?.length > 0 &&
                managerSpoc.map((spoc, index) => (
                  <div
                    key={index}
                    className="flex flex-wrap gap-10 text-sm mb-4"
                  >
                    <div>
                      <p className="text-gray-500 flex items-center gap-2 mb-1">
                        👤 <span>Contact</span>
                      </p>
                      <p className="font-medium text-gray-800">
                        {spoc.contact_name}
                      </p>
                    </div>

                    <div>
                      <p className="text-gray-500 flex items-center gap-2 mb-1">
                        📞 <span>Phone</span>
                      </p>
                      <p className="font-medium text-gray-800">
                        {spoc.contact_mobile}
                      </p>
                    </div>
                  </div>
                ))}
              <hr className="my-4" />

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  👥 Assigned Team (
                  {wh.roles["warehouse operator"]?.length || 0})
                </p>

                <div className="flex flex-wrap gap-2">
                  {wh.roles["warehouse operator"]?.slice(0, 3).map((spoc) => (
                    <span
                      key={spoc.uid}
                      className="max-w-full truncate px-3 py-1 bg-gray-100 rounded-full text-sm font-medium text-gray-700"
                    >
                      {spoc.contact_name}
                    </span>
                  ))}

                  {wh.roles["warehouse operator"]?.length > 3 && (
                    <span className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-600 whitespace-nowrap">
                      +{wh.roles["warehouse operator"].length - 3} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {openModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            id="warehouse-management-modal-backdrop"
            className="absolute inset-0 bg-black bg-opacity-40"
            onClick={() => {
              setOpenModal(false);
              setEditAssignment(undefined);
            }}
          />

          <div id="warehouse-management-modal" className="relative z-10 border">
            <AddWarehouseSpocModal
              warehouse={editWarehouse}
              editData={editAssignment}
              spocs={warehouse_spocs}
              onClose={() => {
                setOpenModal(false);
                setEditAssignment(undefined);
              }}
              onSaved={fetchData}
            />
          </div>
        </div>
      )}
    </div>
  );
};
